// guestGate.js — the guest redirect-loop fixes, as PURE functions (2026-08-14).
//
// ⛔ THE CONFIRMED LOOP. On 2026-08-14 a guestmode `asst_` link on relay-assistant-v2 bounced
// /signin ↔ /ibot-guest ~20 times at ~0.7s each. Mechanism, established from robot-api's log
// (20 successful mints) plus the code:
//
//   1. IndexPageGuest's "Token Security" gate reads a `token=` COOKIE and `localStorage
//      .tokenTimestamp`, and redirects to /signin when that timestamp is >24h old.
//   2. It NEVER validates the JWT — only that a cookie named `token` exists.
//   3. `setAuthCookies` set that cookie with NO Max-Age ⇒ a SESSION cookie, which Chrome's
//      session-restore keeps alive indefinitely. A weeks-dead login still presents a cookie.
//   4. `signInGuest` writes neither the cookie nor `tokenTimestamp`, so a guest mint can never
//      clear the condition. 20 successful mints, 20 bounces.
//
// It ended only because switching to an `agent_` id routed GB to the AUTHED sign-in; signing in
// refreshed `tokenTimestamp` (iat and the timestamp share the same second, 19:07:05) and disarmed
// the gate. The id was incidental — authenticating was the fix.
//
// ⚠️ PLAIN JS ON PURPOSE. These are pure and are imported BOTH by the TS/TSX client and by
// `node --test relay-server/test/`. Keeping them un-typed means the gate that actually runs
// executes the REAL code, instead of a type-stripped copy (the pattern bot-config-maps.test.js
// had to use for the resolveCommand twin).

/** Default cookie lifetime when a JWT carries no usable `exp`. Deliberately short. */
export const DEFAULT_COOKIE_MAX_AGE_SEC = 6 * 60 * 60;

/** How many /signin → /ibot-guest round trips before we stop and say so. */
export const MAX_GUEST_SIGNIN_ATTEMPTS = 3;

/** sessionStorage key for the bound. Per-tab and per-session, which is the right blast radius. */
export const GUEST_LOOP_ATTEMPTS_KEY = 'guestSignInAttempts';

/**
 * FIX 1 — does the AUTHED timestamp gate apply to this session?
 *
 * ⛔ No, when the session is a guest. A guest's validity is the mint's concern; the authed
 * `tokenTimestamp` has no jurisdiction over it, and letting it redirect a guest is the loop.
 * A leftover cookie from a previous AUTHED login on the same origin is exactly the state that
 * makes this fire, and no guest action can ever clear it.
 */
export function authTimestampGateApplies({ guestMode, cookieToken, tokenTimestamp } = {}) {
  if (guestMode === 'true' || guestMode === true) return false;   // ← the fix
  if (!cookieToken) return false;
  if (!tokenTimestamp) return false;
  return true;
}

/**
 * The gate's age decision, unchanged in threshold — extracted so it is testable and so the
 * guest skip above is visibly separate from it.
 *
 * ⚠️ `substring(0,10)` is the ms→s truncation the original used; it is CORRECT (a 13-digit ms
 * epoch truncates to its 10-digit second epoch) and is preserved deliberately.
 */
export function isAuthTokenStale(tokenTimestamp, nowMs = Date.now(), timeoutSec = 24 * 60 * 60) {
  const n = Number(String(tokenTimestamp ?? '').substring(0, 10));
  if (Number.isNaN(n)) return false;            // unparseable ⇒ never redirect (original behaviour)
  return nowMs / 1000 - n > timeoutSec;
}

/**
 * FIX 3 — the cookie's Max-Age, taken from the JWT's OWN lifetime.
 *
 * ⛔ A session cookie let a dead login masquerade as live across browser restarts. Aligning the
 * cookie to `exp` means the browser drops it exactly when the credential actually dies.
 *
 * ⚠️ This does NOT change any expiry *decision* — nothing here reads `exp` to decide whether to
 * redirect (see the deferred item in the U-notes). It only stops the cookie outliving its token.
 *
 * @returns {number} seconds; falls back to DEFAULT_COOKIE_MAX_AGE_SEC, never returns <= 0
 */
export function cookieMaxAgeFromJwt(jwt, nowMs = Date.now()) {
  try {
    const part = String(jwt || '').split('.')[1];
    if (!part) return DEFAULT_COOKIE_MAX_AGE_SEC;
    const pad = part.length % 4 === 0 ? '' : '='.repeat(4 - (part.length % 4));
    const json = atob((part + pad).replace(/-/g, '+').replace(/_/g, '/'));
    const { exp } = JSON.parse(json);
    if (!exp) return DEFAULT_COOKIE_MAX_AGE_SEC;
    const remaining = Math.floor(exp - nowMs / 1000);
    return remaining > 0 ? remaining : DEFAULT_COOKIE_MAX_AGE_SEC;
  } catch {
    return DEFAULT_COOKIE_MAX_AGE_SEC;
  }
}

/**
 * FIX 4 — bound the /signin ↔ /ibot-guest cycle.
 *
 * ⭐ Deliberately independent of the other three. Fixes 1–3 close THIS loop; the bound closes the
 * FAMILY. Five theories were wrong before the real mechanism was found, and every one of them
 * would still have spun forever — an unbounded cycle turns any future guest-auth bug into an
 * infinite one instead of an error message.
 *
 * @returns {{ attempts: number, exceeded: boolean }}
 */
export function bumpGuestSignInAttempts(storage, max = MAX_GUEST_SIGNIN_ATTEMPTS) {
  try {
    const prev = Number(storage.getItem(GUEST_LOOP_ATTEMPTS_KEY) || '0');
    const attempts = (Number.isNaN(prev) ? 0 : prev) + 1;
    storage.setItem(GUEST_LOOP_ATTEMPTS_KEY, String(attempts));
    return { attempts, exceeded: attempts > max };
  } catch {
    // Storage unavailable (private mode quirks) ⇒ never block the user on our own bookkeeping.
    return { attempts: 0, exceeded: false };
  }
}

/** Clear the bound. Called when a guest session actually starts — success resets the counter. */
export function resetGuestSignInAttempts(storage) {
  try { storage.removeItem(GUEST_LOOP_ATTEMPTS_KEY); } catch { /* no-op */ }
}

/**
 * The in-flight latch, made RECOVERABLE (2026-08-14).
 *
 * ⛔ THE DEFECT. `SignInPageGuest.tsx:194` stored a bare `'1'` and, on seeing it, `return`ed out of
 * the sign-in IIFE **without minting and without redirecting** — the page renders its splash and
 * sits forever. Two clears existed (`:242` a `finally`, `:248` an unmount cleanup) and NEITHER
 * covers the case that strands it: ⚠️ **React does not run effect cleanup when the browser
 * navigates away from the page**, so a user who backs out or closes during the ~0.7s mint leaves
 * `guestSignInInFlight='1'` in sessionStorage for the life of that tab.
 *
 * ⭐ THE FIX IS A TTL, NOT A DELETION. The guard is legitimate — two concurrent mounts really
 * should not both mint. What was wrong is that it was PERMANENT: a boolean cannot distinguish
 * "a request is in flight right now" from "a request was in flight when the tab was abandoned".
 * A timestamp can, and 15s is far longer than a healthy mint (~0.65s measured on 2026-08-14) and
 * far shorter than a human's patience.
 */
export const GUEST_SIGNIN_INFLIGHT_TTL_MS = 15000;

/**
 * Try to claim the sign-in slot.
 * @returns {{ claimed: boolean, staleReclaim: boolean, ageMs: number|null }}
 *   claimed=false  ⇒ a genuine concurrent attempt is running; skip (the original intent)
 *   staleReclaim   ⇒ we took over an abandoned claim, which is the recovery
 */
export function claimGuestSignIn(storage, key, nowMs = Date.now(), ttlMs = GUEST_SIGNIN_INFLIGHT_TTL_MS) {
  try {
    const raw = storage.getItem(key);
    if (raw) {
      // Legacy `'1'` values carry no age. Treat them as STALE rather than honouring them
      // forever — that value is exactly the stranded state this fix exists to clear.
      const started = Number(raw);
      const isLegacyBoolean = Number.isNaN(started) || String(raw) === '1';
      const ageMs = isLegacyBoolean ? null : nowMs - started;
      const stale = isLegacyBoolean || ageMs > ttlMs;
      if (!stale) return { claimed: false, staleReclaim: false, ageMs };
      storage.setItem(key, String(nowMs));
      return { claimed: true, staleReclaim: true, ageMs };
    }
    storage.setItem(key, String(nowMs));
    return { claimed: true, staleReclaim: false, ageMs: null };
  } catch {
    // Storage unavailable ⇒ proceed. Never let our own bookkeeping block a sign-in.
    return { claimed: true, staleReclaim: false, ageMs: null };
  }
}

/** Release the slot. Safe to call twice. */
export function releaseGuestSignIn(storage, key) {
  try { storage.removeItem(key); } catch { /* no-op */ }
}

// ═══════════════════════════════════════════════════════════════════════════════════════
// S2-A — guest identity/attribution (2026-09-18). Design: porting/design-notes/
// S2_IDENTITY_DESIGN.md §2 as amended by Grok's GO-WITH-NOTES review (Notes 1–6, 8).
//
// ⛔ THE DEFECT. `guestSignIn.ts` stamped ONE constant org id — fictional, GB-confirmed — into
// `localStorage.orgId` on EVERY guest mint for EVERY bot. That value rides `metadata.org.orgId`
// to the relay (`relayAPI.js:869`) and onto every chatHistory / BotMetricsClient row.
//
// ⭐ THE PIN. The relay's persistence gate (`relayAPI.js:4798-4807`) SKIPS the chatHistory POST
// when orgId is falsy, so the fix must SUPPLY an orgId — never delete the write sites. When the
// true org cannot be established the correct state is UNATTRIBUTED (`orgId: ''`, loud), never a
// constant, never a placeholder, never "whatever the record happens to hold".
//
// Source of truth (Note 1): `BotSettings.orgId` via `GET /botsettings/{id}` — the home the relay
// reads (`bot-config.js:661`, inside normalizeBotSettingsRecord) — NOT `assistants.orgId`, which
// the migration never wrote. The GB two-home data pass precedes DEPLOY (release gate).
//
// ⚠️ Plain JS on purpose (see the file header): these run under `node --test` as the real code.
// ═══════════════════════════════════════════════════════════════════════════════════════

/** A plausible orgId is a 24-hex Mongo ObjectId. NOTHING else (Note 3): the 26-char KAI 2 typo
 *  and SOLIDleaders' `w348907q…` ARE in BotSettings records today — "appears in the record" is
 *  the hazard, not a check. */
export const OBJECT_ID_RE = /^[0-9a-f]{24}$/i;
export function isPlausibleOrgId(v) {
  return typeof v === 'string' && OBJECT_ID_RE.test(v.trim());
}

/**
 * Decide the org for a guest session from the BotSettings record.
 * @returns {{ orgId: string, source: 'RECORD'|'UNATTRIBUTED', reason: string }}
 *   RECORD        → orgId is the record's 24-hex id
 *   UNATTRIBUTED  → orgId is '' (the relay then skips the chatHistory POST — pinned behaviour);
 *                   `reason` distinguishes DATA (no-record / absent-org / implausible-org) from
 *                   INFRA (fetch-failed, set by resolveGuestOrgWithRetry) — Note 2.
 */
export function resolveGuestOrgId(record) {
  if (!record || typeof record !== 'object') {
    return { orgId: '', source: 'UNATTRIBUTED', reason: 'no-record' };
  }
  const raw = record.orgId;
  const s = raw == null ? '' : String(raw).trim();
  if (s === '') return { orgId: '', source: 'UNATTRIBUTED', reason: 'absent-org' };
  if (!isPlausibleOrgId(s)) {
    return { orgId: '', source: 'UNATTRIBUTED', reason: `implausible-org:${s.length}chars` };
  }
  return { orgId: s, source: 'RECORD', reason: '' };
}

/**
 * Fetch the BotSettings record and resolve. A FETCH FAILURE (network / 5xx / 401) is retried
 * once and then yields UNATTRIBUTED with reason `fetch-failed:<status|message>` — never thrown
 * (Note 2): a botapi blip must not fail the mint, and a 500 is not "this bot has no owner".
 * @param {(id: string, token: string) => Promise<object>} fetchBotSettings
 */
export async function resolveGuestOrgWithRetry(fetchBotSettings, botId, token, { retries = 1 } = {}) {
  if (!botId) return { orgId: '', source: 'UNATTRIBUTED', reason: 'no-bot-id' };
  let lastErr = null;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const record = await fetchBotSettings(botId, token);
      return resolveGuestOrgId(record);
    } catch (err) {
      lastErr = err;
    }
  }
  const status = lastErr?.statusCode ?? lastErr?.response?.status ?? lastErr?.status;
  const what = status != null ? String(status) : String(lastErr?.message ?? 'unknown').slice(0, 60);
  return { orgId: '', source: 'UNATTRIBUTED', reason: `fetch-failed:${what}` };
}

/** The one line an operator can grep for (browser console; also carried in metadata.org). */
export function formatGuestOrgLog(botId, resolution) {
  return `[GuestOrg] bot=${botId ?? '∅'} source=${resolution.source} orgId=${resolution.orgId || '∅'}` +
    (resolution.reason ? ` reason=${resolution.reason}` : '');
}

/**
 * Commit a minted guest session to storage IN THE PINNED ORDER (Note 4): identity/org keys
 * first, `token` and `userId` LAST. `IndexPageGuest`'s 6 s poll releases the socket the moment
 * it sees token+userId; if they landed before the org, the session would open with orgId ''
 * and the relay would skip every row. Callers MUST have awaited the org resolution before
 * calling this.
 * @param {Storage} storage  localStorage (or a fake)
 * @param {object} data      the /auth/guest response ({token, user})
 * @param {object} org       the resolution from resolveGuestOrgId / resolveGuestOrgWithRetry
 * @param {string} botId     the bot the org was resolved for (re-resolve key on bot change, Note 8)
 */
export function commitGuestSession(storage, data, org, botId, nowMs = Date.now()) {
  const set = (k, v) => storage.setItem(k, v);
  set('guestMode', 'true');
  set('tokenTimestamp', String(nowMs));
  set('orgId', org.orgId || '');
  set('orgSource', org.source);
  set('orgBotId', botId || '');
  const stored = { ...(data || {}), org: { ...((data && data.org) || {}), orgId: org.orgId || '', source: org.source, reason: org.reason || '' } };
  set('user', JSON.stringify(stored));
  if (data?.user?.name) set('name', data.user.name);
  // ── token + userId LAST ──
  if (data?.token) set('token', data.token);
  if (data?.user?.id) set('userId', data.user.id);
  return stored;
}

/** Write a refreshed token for the SAME user (POST /auth/guest/refresh, Note 5). Never touches
 *  orgId / userId. */
export function commitRefreshedToken(storage, token, nowMs = Date.now()) {
  storage.setItem('token', token);
  storage.setItem('tokenTimestamp', String(nowMs));
  try {
    const stored = JSON.parse(storage.getItem('user') || '{}');
    stored.token = token;
    storage.setItem('user', JSON.stringify(stored));
  } catch { /* stored-user merge is best effort */ }
}

/** Seconds until the JWT's `exp`; null when missing/unparseable. Pure twin of isGuestTokenExpired. */
export function jwtSecondsRemaining(jwt, nowMs = Date.now()) {
  try {
    const part = String(jwt || '').split('.')[1];
    if (!part) return null;
    const pad = part.length % 4 === 0 ? '' : '='.repeat(4 - (part.length % 4));
    const json = atob((part + pad).replace(/-/g, '+').replace(/_/g, '/'));
    const { exp } = JSON.parse(json);
    if (!exp) return null;
    return Math.floor(exp - nowMs / 1000);
  } catch {
    return null;
  }
}

/** Refresh when exp − now < this (seconds). Well inside the 6 h guest JWT, well outside a turn. */
export const GUEST_REFRESH_SKEW_SEC = 15 * 60;

/**
 * The stale-auth key set (F5 writers table, S2 note §2.6.4, + Grok note (2)-7). Purged on BOTH
 * pages before any redirect to /signin or /guest, and on SignInPageGuest mount, so no leftover
 * from a previous session can re-arm the redirect loop. EXACTLY this set — a test pins it.
 */
export const STALE_AUTH_LOCAL_KEYS = [
  'tokenTimestamp', 'token', 'user', 'guestMode', 'orgId',
  'guestSignInCooldownUntil', 'guestOrigin',
];
export const STALE_AUTH_SESSION_KEYS = ['guestSignInInFlight', 'guestOrigin'];
export const STALE_AUTH_COOKIES = ['token', 'userId'];

/**
 * @param {{localStorage?: Storage, sessionStorage?: Storage, document?: {cookie: string}}} ctx
 * @param {{keep?: string[]}} opts  keys to leave alone (SignInPageGuest keeps the 429 cooldown +
 *                                  the in-flight latch it is about to claim)
 * @returns {{removed: string[]}}
 */
export function purgeStaleAuthKeys(ctx = {}, { keep = [] } = {}) {
  const removed = [];
  const skip = new Set(keep);
  const rm = (storage, key, label) => {
    if (!storage || skip.has(key)) return;
    try {
      if (storage.getItem(key) !== null) removed.push(`${label}:${key}`);
      storage.removeItem(key);
    } catch { /* storage unavailable */ }
  };
  for (const k of STALE_AUTH_LOCAL_KEYS) rm(ctx.localStorage, k, 'local');
  for (const k of STALE_AUTH_SESSION_KEYS) rm(ctx.sessionStorage, k, 'session');
  if (ctx.document) {
    for (const c of STALE_AUTH_COOKIES) {
      if (skip.has(c)) continue;
      try { ctx.document.cookie = `${c}=; path=/; Max-Age=0`; removed.push(`cookie:${c}`); } catch { /* no-op */ }
    }
  }
  return { removed };
}

/**
 * [ibot-nosession] The `token` cookie's value, or '' — the same parse every page does inline
 * (`document.cookie.split('; ').find(r => r.startsWith('token='))?.split('=')[1]`).
 */
export function readCookieToken(cookieString) {
  return String(cookieString || '').split('; ').find((r) => r.startsWith('token='))?.split('=')[1] || '';
}

/**
 * [ibot-nosession] "No usable session" — the tree's ONE definition, extracted verbatim from
 * IndexPageGuest.tsx's absent-token gate (`const absent = !stored && !cookieToken;`, S2-A §2.6.1):
 * no `localStorage.token` AND no `token` cookie. ⚠️ A present-but-EXPIRED token is NOT absent —
 * that case keeps its own bounded path (the Token Security gate, 95227ae / S2-A FIX 4).
 * A test pins the guest page's inline expression so the two cannot drift.
 */
export function hasNoSessionToken({ localStorage, document } = {}) {
  let stored = null;
  try { stored = localStorage?.getItem('token') ?? null; } catch { /* storage unavailable */ }
  return !stored && !readCookieToken(document?.cookie);
}

/** /signin on this origin carrying the page's WHOLE query string (id, prompt, ses, ctx, …). */
export function signInRedirectUrl(origin, search) {
  const url = new URL(origin);
  url.pathname = '/signin';
  url.search = search || '';
  return url.toString();
}

/**
 * [ibot-nosession] /ibot's pre-fetch session gate (2026-09-23). A handed-out /ibot?id= link with
 * no session worked only because botapi's assistants routes were unauthenticated; once they
 * require a token that load would render "bot unavailable". Instead: ONE purged, bounded redirect
 * to /signin with the query intact — the FIX 4 machinery, so a misconfigured /signin stops at
 * MAX_GUEST_SIGNIN_ATTEMPTS with the terminal screen instead of looping.
 *
 * @param {{localStorage, sessionStorage, document, location: {origin: string, search: string}}} ctx
 * @param {{navigate: (url: string) => void, onStop: (attempts: number) => void}} deps
 * @returns {'continue'|'redirected'|'stopped'} anything but 'continue' ⇒ the caller returns
 *   BEFORE the record fetch.
 */
export function enforceIbotSession(ctx = {}, { navigate, onStop } = {}) {
  if (!hasNoSessionToken(ctx)) return 'continue';
  const { attempts, exceeded } = bumpGuestSignInAttempts(ctx.sessionStorage);
  if (exceeded) {
    onStop?.(attempts);
    return 'stopped';
  }
  purgeStaleAuthKeys({ localStorage: ctx.localStorage, sessionStorage: ctx.sessionStorage, document: ctx.document });
  navigate?.(signInRedirectUrl(ctx.location?.origin, ctx.location?.search));
  return 'redirected';
}
